import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "access-invite__content" }
const _hoisted_2 = {
  key: 0,
  class: "access-invite__client-info"
}
const _hoisted_3 = { class: "access-invite__section-title" }
const _hoisted_4 = { class: "access-invite__info-grid" }
const _hoisted_5 = { class: "access-invite__info-item" }
const _hoisted_6 = { class: "access-invite__info-value" }
const _hoisted_7 = { class: "access-invite__info-item" }
const _hoisted_8 = { class: "access-invite__info-value" }
const _hoisted_9 = {
  key: 1,
  class: "access-invite__form-row"
}
const _hoisted_10 = { class: "access-invite__form-group" }
const _hoisted_11 = { for: "clientName" }
const _hoisted_12 = { class: "access-invite__input-with-icon" }
const _hoisted_13 = ["placeholder"]
const _hoisted_14 = {
  key: 0,
  class: "access-invite__error-message",
  role: "alert"
}
const _hoisted_15 = {
  key: 1,
  class: "access-invite__form-hint"
}
const _hoisted_16 = { class: "access-invite__form-group" }
const _hoisted_17 = { for: "clientEmail" }
const _hoisted_18 = { class: "access-invite__input-with-icon" }
const _hoisted_19 = ["placeholder"]
const _hoisted_20 = {
  key: 0,
  class: "access-invite__error-message",
  role: "alert"
}
const _hoisted_21 = { class: "access-invite__form-group" }
const _hoisted_22 = { for: "accessRights" }
const _hoisted_23 = { class: "access-invite__access-rights-grid" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "access-invite__access-option-content" }
const _hoisted_26 = { class: "access-invite__access-option-checkbox" }
const _hoisted_27 = ["id", "value", "checked", "onChange"]
const _hoisted_28 = ["for"]
const _hoisted_29 = {
  key: 0,
  class: "access-invite__access-option-icon"
}
const _hoisted_30 = {
  viewBox: "0 0 24 24",
  width: "20",
  height: "20"
}
const _hoisted_31 = ["d"]
const _hoisted_32 = { class: "access-invite__access-option-title" }
const _hoisted_33 = {
  key: 0,
  class: "access-invite__error-message",
  role: "alert"
}
const _hoisted_34 = { class: "access-invite__dialog-actions" }

import { ref, reactive, watch, computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import AppModal from '@/components/base/Modal.vue';
import AppButton from '@/components/base/AppButton.vue';
import PermissionGuard from '@/components/common/PermissionGuard.vue';
import ProjectAccessService from '@/services/projectAccess.service';
import { usePermissions } from '@/composables/usePermissions';
import { PermissionType } from '@/services/permission.service';
import type { User } from '@/types';

// Props
interface Props {
  show: boolean;
  projectId: string | number;
  isEditMode?: boolean;
  accessId?: string | number | null;
  accessData?: Record<string, any>;
}

interface Emits {
  (e: 'close'): void;
  (e: 'user-added', user: User): void;
  (e: 'invite-sent'): void;
  (e: 'access-updated'): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AccessInvite',
  props: {
    show: { type: Boolean, default: false },
    projectId: {},
    isEditMode: { type: Boolean, default: false },
    accessId: { default: null },
    accessData: { default: () => ({}) }
  },
  emits: ["close", "user-added", "invite-sent", "access-updated"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

// Expose commonly-used props to the template as refs (template unwraps refs)
const { show, isEditMode } = toRefs(props);

// Emits
const emit = __emit;

// Composables
const { t } = useI18n();
// Permissions composable must be called during setup (not later in callbacks)
const { hasPermissionForProject } = usePermissions();

// Reactive state
const firstInput = ref<HTMLInputElement | null>(null);
const isSubmitting = ref(false);
// Use centralized notifications
const { notifySuccess, notifyError } = useNotifications();
const invitationForm = reactive({
  clientName: '',
  email: '',
  accessRights: [] as string[]
});

const editForm = reactive({
  accessRights: [] as string[]
});

const validationErrors = reactive({
  clientName: '',
  email: '',
  accessRights: ''
});

// Icons for access rights - Material Design icons
const iconSet = {
  tasks: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19M17,17H7V7H17V17M15,15H9V9H15V15',
  phases: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19M17,17H7V7H17V17M15,15H9V9H15V15',
  photos: 'M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M6,20H18V12L14,16L12,14L6,20M8,9A2,2 0 0,0 6,11A2,2 0 0,0 8,13A2,2 0 0,0 10,11A2,2 0 0,0 8,9Z',
  documents: 'M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M11,4H6V20H18V11H11V4Z',
  budget: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
  intervenants: 'M16,13C15.71,13 15.38,13 15.03,13.05C16.19,13.89 17,15 17,16.5V19H23V16.5C23,14.17 18.33,13 16,13M8,13C5.67,13 1,14.17 1,16.5V19H15V16.5C15,14.17 10.33,13 8,13M8,11A3,3 0 0,0 11,8A3,3 0 0,0 8,5A3,3 0 0,0 5,8A3,3 0 0,0 8,11M16,11A3,3 0 0,0 19,8A3,3 0 0,0 16,5A3,3 0 0,0 13,8A3,3 0 0,0 16,11Z',
  notes: 'M14,10V4.5L19.5,10M5,3C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V9L15,3H5Z',
  design: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z',
  viewReport: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M7,17V7H9V17H7M11,17V11H13V17H11M15,17V14H17V17H15Z',
  audit: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M7,17V7H9V17H7M11,17V11H13V17H11M15,17V14H17V17H15Z',
  viewIssues: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,6H13V13H11V6M11,16H13V18H11V16Z'
};

// Access rights options with descriptions
const accessOptions = [
  {
    value: 'MANAGE_TASKS',
    label: t('projects.manageTasks'),
    icon: iconSet.tasks
  },
  {
    value: 'PHASES',
    label: t('projects.managePhases'),
    icon: iconSet.phases
  },
  {
    value: 'PHOTOS',
    label: t('projects.photosAction'),
    icon: iconSet.photos
  },
  {
    value: 'DOCUMENTS',
    label: t('projects.documentsAction'),
    icon: iconSet.documents
  },
  {
    value: 'BUDGET',
    label: t('projects.budgetAction'),
    icon: iconSet.budget
  },
  {
    value: 'INTERVENANTS',
    label: t('projects.intervenantsAction'),
    icon: iconSet.intervenants
  },
  {
    value: 'NOTES',
    label: t('projects.notesAction'),
    icon: iconSet.notes
  },
  {
    value: 'DESIGN',
    label: t('projects.designAction'),
    icon: iconSet.design
  },
  {
    value: 'REPORTS',
    label: t('projects.viewReport'),
    icon: iconSet.viewReport
  },
  {
    value: 'ISSUES',
    label: t('projects.issuesAction'),
    icon: iconSet.viewIssues
  },
  {
    value: 'AUDIT',
    label: t('projects.auditTrail'),
    icon: iconSet.audit
  }
];

// Helper function to get access rights values.
// When editing, prefer the granted rights from props.accessData.accessRights (filtered to known options).
// Otherwise return all available option values (create mode default).
const getAllAccessRights = () => {
  // Determine which option values are allowed by the current user's permissions
  const allowedOptions = accessOptions.filter(option => {
    const perm = getPermissionForOption(option.value);
    // If there is no mapped permission, include by default
    if (!perm) return true;
    // Use composable's project-scoped permission check (computed ref) and read its value
    try {
      return !!hasPermissionForProject(perm as PermissionType, props.projectId).value;
    } catch (e) {
      console.warn('getAllAccessRights: permission check failed for', perm, e);
      return false;
    }
  }).map(o => o.value);

  // If editing and server provided granted rights, return their intersection with allowedOptions
  if (props.isEditMode && props.accessData && Array.isArray(props.accessData.accessRights)) {
    const granted: string[] = props.accessData.accessRights as string[]
    const allowedSet = new Set(allowedOptions)
    return granted.filter(r => allowedSet.has(r))
  }

  // Default (create mode): return allowed options
  return allowedOptions
}

// Map access option value to frontend permission name
const getPermissionForOption = (optionValue: string) => {
  switch (optionValue) {
    case 'MANAGE_TASKS':
      return 'ACCESS_TASKS';
    case 'PHOTOS':
      return 'ACCESS_PHOTO';
    case 'DOCUMENTS':
      return 'ACCESS_DOCUMENT';
    case 'BUDGET':
      return 'ACCESS_FINANCE';
    case 'INTERVENANTS':
      return 'ACCESS_INTERVENANTS';
    case 'NOTES':
      return 'NOTES';
    case 'DESIGN':
      return 'ACCESS_3D_DESIGN';
    case 'REPORTS':
      return 'VIEW_REPORTS';
    default:
      return null;
  }
};

// Computed property to get current access rights based on mode
const currentAccessRights = computed(() => {
  return props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
});

// Close the dialog
const closeDialog = () => {
  resetForm();
  console.log('AccessInvite: closeDialog called');
  console.log('AccessInvite: Before emit, show prop is:', props.show);
  emit('update:show', false);
  console.log('AccessInvite: After emit update:show false');
};

// Reset form fields
const resetForm = () => {
  // Reset invitation form
  invitationForm.clientName = '';
  invitationForm.email = '';
  invitationForm.accessRights = [];
  
  // Reset edit form
  editForm.accessRights = [];
  
  // Clear validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  // Note: notifications are global, no local notification state to clear
};

// Toggle access right selection
const toggleAccessRight = (value: string) => {
  const targetForm = props.isEditMode ? editForm : invitationForm;
  const index = targetForm.accessRights.indexOf(value);
  if (index === -1) {
    targetForm.accessRights.push(value);
  } else {
    targetForm.accessRights.splice(index, 1);
  }
  validateOnInput('accessRights');
};

// Validate single field on input
const validateOnInput = (field: string) => {
  // When the user starts fixing issues we don't keep a local global error; centralized notifications are external

  switch (field) {
    case 'clientName': {
      validationErrors.clientName = '';
      const trimmedName = invitationForm.clientName.trim();
      
      if (trimmedName && trimmedName.length < 3) {
        validationErrors.clientName = t('projects.clientNameMinLength', 'Client name must be at least 3 characters');
      } else if (trimmedName && /[<>\\{}]/.test(trimmedName)) {
        validationErrors.clientName = t('projects.clientNameInvalidChars', 'Client name contains invalid characters');
      }
      break;
    }
      
    case 'email': {
      validationErrors.email = '';
      const trimmedEmail = invitationForm.email.trim();
      
      if (trimmedEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
          validationErrors.email = t('projects.invalidEmail', 'Please enter a valid email address');
        } else if (trimmedEmail.length > 100) {
          validationErrors.email = t('projects.emailTooLong', 'Email address is too long');
        }
      }
      break;
    }    case 'accessRights': {
      validationErrors.accessRights = '';
      // Get current form's access rights
      const currentAccessRights = props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
      // Show validation message if access rights are selected then unselected
      if (currentAccessRights.length === 0) {
        validationErrors.accessRights = t('projects.accessRightsRequired', 'Please select at least one access right');
      }
      break;
    }
  }
};

// Validate form
const validateForm = () => {
  let isValid = true;
  
  // Reset validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  
  // Only validate client fields in create mode
  if (!props.isEditMode) {
    // Validate client name
    if (!invitationForm.clientName.trim()) {
      validationErrors.clientName = t('projects.clientNameRequired');
      isValid = false;
      firstInput.value?.focus();
    } else if (invitationForm.clientName.trim().length < 3) {
      validationErrors.clientName = t('projects.clientNameMinLength');
      isValid = false;
      firstInput.value?.focus();
    }
    
    // Validate email
    if (!invitationForm.email.trim()) {
      validationErrors.email = t('projects.emailRequired');
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(invitationForm.email.trim())) {
        validationErrors.email = t('projects.invalidEmail');
        isValid = false;
      }
    }
  }
  
  // Validate access rights - at least one must be selected (for both modes)
  const currentAccessRights = props.isEditMode ? editForm.accessRights : invitationForm.accessRights;
  if (currentAccessRights.length === 0) {
    validationErrors.accessRights = t('projects.accessRightsRequired');
    isValid = false;
  }
  
  return isValid;
};

const handleClick = () => {
  if (props.isEditMode) {
    submitUpdate();
  } else {
    submitInvitation();
  }
};

// Submit invitation
const submitInvitation = async () => {
  // Clear any previous validation state before submission
  
  // Reset validation errors
  validationErrors.clientName = '';
  validationErrors.email = '';
  validationErrors.accessRights = '';
  
  // Validate form
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    await ProjectAccessService.inviteClient({
      projectId: props.projectId,
      clientName: invitationForm.clientName.trim(),
      email: invitationForm.email.trim(),
      accessRights: invitationForm.accessRights
    });
    
  // Show success notification via centralized system
  notifySuccess(t('projects.invitationSentSuccessfully', 'Invitation sent successfully!'));
    
    // Emit event to notify parent
    emit('invite-sent');

    // Reset form (but don't close dialog right away so user can see success message)
    invitationForm.clientName = '';
    invitationForm.email = '';
    invitationForm.accessRights = getAllAccessRights();
    
    // Close dialog after a short delay to allow user to see the success message
    setTimeout(() => {
      closeDialog();
    }, 2000);
  } catch (error: any) {
    console.error('Error sending invitation:', error);
    
    // Handle validation errors from backend
    if (error.response?.data) {
      const responseData = error.response.data;

      // Handle field-specific validation errors
      if (responseData.field) {
        // Map backend field names to frontend field names
        const fieldMap: Record<string, string> = {
          'clientName': 'clientName',
          'email': 'email', 
          'accessRights': 'accessRights',
          // Add other fields mappings as needed
        };
        
        const fieldName = fieldMap[responseData.field] || responseData.field;

        // Apply error to the specific field if we have a mapping for it
        if (fieldName in validationErrors) {
          (validationErrors as any)[fieldName] = responseData.message || t('errors.validationError');
          
          // For accessRights, handle specific validation failures
          if (fieldName === 'accessRights') {
            // Highlight the field to draw attention to it
            const accessRightsContainer = document.querySelector('.access-invite__access-rights-grid');
            if (accessRightsContainer) {
              accessRightsContainer.classList.add('access-invite__validation-shake');
              setTimeout(() => {
                accessRightsContainer.classList.remove('access-invite__validation-shake');
              }, 820);
            }
          }
        } else {
          // Unknown field, show via centralized notifications
          notifyError(`${responseData.field}: ${responseData.message}`);
        }
      }
      // Handle multiple validation errors (Spring Boot validation style)
      else if (responseData.errors && Array.isArray(responseData.errors)) {
        // Process all validation errors
        responseData.errors.forEach((err: any) => {
          if (err.field === 'email') {
            validationErrors.email = err.message || err.defaultMessage;
          } else if (err.field === 'clientName') {
            validationErrors.clientName = err.message || err.defaultMessage;
          } else if (err.field === 'accessRights') {
            validationErrors.accessRights = err.message || err.defaultMessage;
          } else {
            // For unknown fields, show each as a global notification
            notifyError(`${err.field}: ${err.message || err.defaultMessage}`);
          }
        });
      } 
      // Handle general error message
      else if (responseData.message) {
        notifyError(responseData.message);
      }
      // If we get here, we've got a response but can't parse it in any known format
      else {
        notifyError(t('errors.unexpectedServerResponse', 
          'Unexpected server response. Please try again later.'));
      }
    } else if (error.message) {
      // Network or other client-side error
      let errorMsg = t('errors.connectionError', 'Connection error. Please check your internet connection and try again.');
      
      // Provide more specific error messages based on common errors
      if (error.message.includes('timeout')) {
        errorMsg = t('errors.requestTimeout', 
          'The request timed out. The server might be busy, please try again later.');
      } else if (error.message.includes('Network Error')) {
        errorMsg = t('errors.networkError', 
          'Network connection error. Please check your internet connection and try again.');
      } else if (error.response?.status === 429) {
        errorMsg = t('errors.tooManyRequests', 
          'Too many requests. Please wait a moment before trying again.');
      }
      
      notifyError(errorMsg);
    } else {
      // Fallback for unknown errors
      notifyError(t('errors.unknownError', 
        'An unexpected error occurred. Please try again later.'));
    }  } finally {
    isSubmitting.value = false;
  }
};

// Submit access update for edit mode
const submitUpdate = async () => {
  // Clear any previous validation state before submission
  
  // Reset validation errors
  validationErrors.accessRights = '';
  
  // Validate form
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    await ProjectAccessService.updateAccess(props.projectId, props.accessId, {
      clientName: props.accessData.clientName,
      accessRights: editForm.accessRights
    });
    
  // Show success notification via centralized system
  notifySuccess(t('projects.accessUpdatedSuccessfully', 'Access rights updated successfully!'));
    
    // Emit event to notify parent
    emit('access-updated');

    // Close dialog after a short delay to allow user to see the success message
    setTimeout(() => {
      closeDialog();
    }, 2000);
  } catch (error: any) {
    console.error('Error updating access:', error);
    
    // Handle validation errors from backend
    if (error.response?.data) {
      const responseData = error.response.data;

      // Handle field-specific validation errors
      if (responseData.field) {
        if (responseData.field === 'accessRights') {
          validationErrors.accessRights = responseData.message || t('errors.validationError');
          
          // Highlight the field to draw attention to it
          const accessRightsContainer = document.querySelector('.access-invite__access-rights-grid');
          if (accessRightsContainer) {
            accessRightsContainer.classList.add('access-invite__validation-shake');
            setTimeout(() => {
              accessRightsContainer.classList.remove('access-invite__validation-shake');
            }, 820);
          }
        } else {
          // Unknown field, show via centralized notifications
          notifyError(`${responseData.field}: ${responseData.message}`);
        }
      }
      // Handle multiple validation errors
      else if (responseData.errors && Array.isArray(responseData.errors)) {
        responseData.errors.forEach((err: any) => {
          if (err.field === 'accessRights') {
            validationErrors.accessRights = err.message || err.defaultMessage;
            } else {
            // For unknown fields, show each as a global notification
            notifyError(`${err.field}: ${err.message || err.defaultMessage}`);
          }
        });
      } 
      // Handle general error message
      else if (responseData.message) {
        notifyError(responseData.message);
      }
      // If we get here, we've got a response but can't parse it in any known format
      else {
        notifyError(t('errors.unexpectedServerResponse', 
          'Unexpected server response. Please try again later.'));
      }
    } else if (error.message) {
      // Network or other client-side error
      let errorMsg = t('errors.connectionError', 'Connection error. Please check your internet connection and try again.');
      
      // Provide more specific error messages based on common errors
      if (error.message.includes('timeout')) {
        errorMsg = t('errors.requestTimeout', 
          'The request timed out. The server might be busy, please try again later.');
      } else if (error.message.includes('Network Error')) {
        errorMsg = t('errors.networkError', 
          'Network connection error. Please check your internet connection and try again.');
      } else if (error.response?.status === 429) {
        errorMsg = t('errors.tooManyRequests', 
          'Too many requests. Please wait a moment before trying again.');
      }
      
      notifyError(errorMsg);
    } else {
      // Fallback for unknown errors
      notifyError(t('errors.unknownError', 
        'An unexpected error occurred. Please try again later.'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Set up focus trap when dialog is shown
watch(() => props.show, (newVal: boolean) => {
  if (newVal) {
    // Reset form and clear errors
    resetForm();
    
    // Initialize edit mode data if in edit mode
    if (props.isEditMode && props.accessData) {
      // Initialize edit form with current access rights
      editForm.accessRights = [...(props.accessData.accessRights || [])];
    } else {
      // Set all access rights as checked by default for create mode
      invitationForm.accessRights = getAllAccessRights();
    }
    
    // Focus first input after dialog is shown (only in create mode)
    setTimeout(() => {
      if (!props.isEditMode && firstInput.value) {
        firstInput.value.focus();
      }
    }, 100);
  } else {
    // When the dialog is hidden (parent closed it or v-model changed), ensure form state is cleared
    resetForm();
  }
});

// Watch for changes in validation errors to clear global error
watch([() => validationErrors.clientName, () => validationErrors.email, () => validationErrors.accessRights], 
  (newValues: string[], oldValues: string[]) => {
    // No local global error to clear; validation errors are kept local. Centralized notifications are independent.
  });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _unref(show),
    onClose: closeDialog,
    title: _unref(isEditMode) ? _ctx.$t('projects.editAccess') : _ctx.$t('projects.inviteClient'),
    icon: _unref(isEditMode) ? 'M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z' : 'M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z',
    size: "large"
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_34, [
        _createVNode(AppButton, {
          variant: "secondary",
          onClick: closeDialog
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(PermissionGuard, {
          permission: 'MANAGE_PROJECT_ACCESS',
          projectId: props.projectId
        }, {
          default: _withCtx(() => [
            _createVNode(AppButton, {
              variant: "primary",
              type: "button",
              disabled: isSubmitting.value,
              loading: isSubmitting.value,
              onClick: handleClick
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isSubmitting.value ? (_unref(isEditMode) ? _ctx.$t('common.updating') : _ctx.$t('common.sending')) : (_unref(isEditMode) ? _ctx.$t('projects.updateAccess') : _ctx.$t('projects.sendInvitation'))), 1)
              ]),
              _: 1
            }, 8, ["disabled", "loading"])
          ]),
          _: 1
        }, 8, ["projectId"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("form", {
          onSubmit: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
          class: "access-invite__form"
        }, [
          (_unref(isEditMode))
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('projects.clientInformation')), 1),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("label", null, _toDisplayString(_ctx.$t('projects.clientName')), 1),
                    _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.accessData.clientName), 1)
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("label", null, _toDisplayString(_ctx.$t('projects.emailAddress')), 1),
                    _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.accessData.email), 1)
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (!_unref(isEditMode))
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("label", _hoisted_11, [
                    _createTextVNode(_toDisplayString(_ctx.$t('projects.clientName')) + " ", 1),
                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "access-invite__required" }, "*", -1))
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _cache[6] || (_cache[6] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "18",
                      height: "18",
                      class: "access-invite__input-icon",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                      })
                    ], -1)),
                    _withDirectives(_createElementVNode("input", {
                      id: "clientName",
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((invitationForm.clientName) = $event)),
                      type: "text",
                      placeholder: _ctx.$t('projects.clientNamePlaceholder'),
                      ref_key: "firstInput",
                      ref: firstInput,
                      onInput: _cache[1] || (_cache[1] = ($event: any) => (validateOnInput('clientName'))),
                      class: _normalizeClass({'access-invite__has-error': validationErrors.clientName}),
                      required: ""
                    }, null, 42, _hoisted_13), [
                      [_vModelText, invitationForm.clientName]
                    ])
                  ]),
                  (validationErrors.clientName)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(validationErrors.clientName), 1))
                    : (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(_ctx.$t('projects.clientNameHint')), 1))
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("label", _hoisted_17, [
                    _createTextVNode(_toDisplayString(_ctx.$t('projects.emailAddress')) + " ", 1),
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "access-invite__required" }, "*", -1))
                  ]),
                  _createElementVNode("div", _hoisted_18, [
                    _cache[8] || (_cache[8] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "18",
                      height: "18",
                      class: "access-invite__input-icon",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"
                      })
                    ], -1)),
                    _withDirectives(_createElementVNode("input", {
                      id: "clientEmail",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((invitationForm.email) = $event)),
                      type: "email",
                      placeholder: _ctx.$t('projects.emailPlaceholder'),
                      onInput: _cache[3] || (_cache[3] = ($event: any) => (validateOnInput('email'))),
                      class: _normalizeClass({'access-invite__has-error': validationErrors.email}),
                      required: ""
                    }, null, 42, _hoisted_19), [
                      [_vModelText, invitationForm.email]
                    ])
                  ]),
                  (validationErrors.email)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, _toDisplayString(validationErrors.email), 1))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("label", _hoisted_22, [
              _createTextVNode(_toDisplayString(_ctx.$t('projects.accessRights')) + " ", 1),
              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "access-invite__required" }, "*", -1))
            ]),
            _createElementVNode("div", _hoisted_23, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(accessOptions, (option) => {
                return _createVNode(PermissionGuard, {
                  key: option.value,
                  permission: getPermissionForOption(option.value),
                  projectId: props.projectId
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", {
                      class: _normalizeClass(["access-invite__access-option", {'access-invite__selected': currentAccessRights.value.includes(option.value)}]),
                      onClick: ($event: any) => (toggleAccessRight(option.value))
                    }, [
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("div", _hoisted_26, [
                          _createElementVNode("input", {
                            type: "checkbox",
                            id: 'access-' + option.value,
                            value: option.value,
                            checked: currentAccessRights.value.includes(option.value),
                            onChange: ($event: any) => (toggleAccessRight(option.value))
                          }, null, 40, _hoisted_27)
                        ]),
                        _createElementVNode("label", {
                          class: "access-invite__access-option-label",
                          for: 'access-' + option.value
                        }, [
                          (option.icon)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_29, [
                                (_openBlock(), _createElementBlock("svg", _hoisted_30, [
                                  _createElementVNode("path", {
                                    fill: "currentColor",
                                    d: option.icon
                                  }, null, 8, _hoisted_31)
                                ]))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("span", _hoisted_32, _toDisplayString(option.label), 1)
                        ], 8, _hoisted_28)
                      ])
                    ], 10, _hoisted_24)
                  ]),
                  _: 2
                }, 1032, ["permission", "projectId"])
              }), 64))
            ]),
            (validationErrors.accessRights)
              ? (_openBlock(), _createElementBlock("div", _hoisted_33, _toDisplayString(validationErrors.accessRights), 1))
              : _createCommentVNode("", true)
          ])
        ], 32)
      ])
    ]),
    _: 1
  }, 8, ["show", "title", "icon"]))
}
}

})